import React, { useState } from 'react';
import WorldMap from './WorldMap.jsx';
import { TIERS, MAX_ROUND_SCORE, ROUNDS_PER_GAME } from './scoring.mjs';
import { formatDistance, formatCoords, localSolarTime } from './format.mjs';
import { buildShareText, distanceEmoji } from './shareText.mjs';
import { useSettings } from './SettingsMenu.jsx';

export default function GameSummary({ tier, rounds, total, rings, practice, onPlayAgain }) {
  const [shared, setShared] = useState(null);
  const settings = useSettings();

  const shareText = buildShareText({
    tier: TIERS[tier].label,
    rounds,
    total,
    practice,
    units: settings.units
  });

  /*
   * Clipboard writes are refused often enough — an insecure origin, a browser
   * that wants a fresher gesture — that the failure has to lead somewhere. On a
   * refusal the text is shown in a selectable box instead of vanishing behind an
   * error nobody can act on.
   */
  const share = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setShared('copied');
    } catch {
      setShared('manual');
    }
  };

  return (
    <div className="sg-summary">
      {/*
        The share action sits under the score, which is what it copies. The moons
        moved onto the round rows, where each one sits beside the distance it
        stands for instead of being a separate row restating all three.
      */}
      <header className="sg-summary-head">
        <p className="sg-summary-tier">{TIERS[tier].label}</p>
        <h2>
          {total.toLocaleString()}{' '}
          <span className="sg-muted">/ {(MAX_ROUND_SCORE * ROUNDS_PER_GAME).toLocaleString()}</span>
        </h2>
        {practice && (
          <p className="sg-practice-note">
            Random places, not today&rsquo;s game &mdash; this one is not ranked.
          </p>
        )}
        <button type="button" className="sg-share-button" onClick={share}>
          {shared === 'copied' ? '✓ Copied' : 'Share result'}
        </button>
        {shared === 'manual' && (
          <textarea className="sg-share-text" readOnly rows={3} value={shareText} onFocus={(e) => e.target.select()} />
        )}
      </header>

      <ol className="sg-summary-rounds">
        {rounds.map((entry, index) => (
          <li key={index}>
            <span className="sg-summary-index">{index + 1}</span>
            <span className="sg-summary-moon" aria-hidden="true">
              {distanceEmoji(Math.round(entry.result.distanceKm))}
            </span>
            {/*
              Where and when, rather than the date: every round of a game is set
              on the day being played, so the date says nothing, while the local
              time at the answer is the thing you were reading off the sky.
            */}
            <span className="sg-summary-detail">
              <strong>{formatDistance(entry.result.distanceKm, settings.units)}</strong> off
              <span className="sg-muted">
                {' '}&middot; {formatCoords(entry.round.lat, entry.round.lon)} &middot;{' '}
                {localSolarTime(entry.round.date, entry.round.lon)} local
              </span>
            </span>
            <span className="sg-summary-points">{entry.result.roundScore.toLocaleString()}</span>
          </li>
        ))}
      </ol>

      <div className="sg-summary-map">
        <WorldMap
          rings={rings}
          pairs={rounds.map((entry, index) => ({
            guess: entry.guess,
            actual: { lat: entry.round.lat, lon: entry.round.lon },
            label: index + 1
          }))}
        />
      </div>

      <button type="button" className="sg-secondary" onClick={onPlayAgain}>
        Play again
      </button>
    </div>
  );
}
